import { FormInstance } from 'antd';
import { Rule } from 'rc-field-form/lib/interface';
import { useTranslation } from 'react-i18next';
import { MeasureType } from 'domains/measurementDomain';

export const RULE_INTEGER: Rule = {
  type: 'integer',
  transform: (v) => Number(v || 0),
};
export const RULE_NUMBER: Rule = {
  type: 'number',
  transform: (v) => Number(v || 0),
};
export const RULE_NUMBER_GT: (than: number) => Rule = (than) => ({
  type: 'number',
  min: than,
  transform: (v) => Number(v || 0),
});
export const RULE_EMAIL: Rule = {
  type: 'email',
};
export const RULE_PRECISION: (precision: number) => Rule = (
  precision: number
) => {
  const { t } = useTranslation();
  return {
    pattern: new RegExp(`^\\d+(\\.\\d{0,${precision}})?$`),
    message: t('common.precisionAllowed', { precision }),
  };
};
/**
 * Decimal-places rule for a field holding a measurement.
 *
 * The plain {@link RULE_PRECISION} tests the raw form value, which is always imperial. A metric user
 * typing 210mm stores 8.267716535433072", so that rule would reject perfectly valid input. This one
 * judges the number the user actually sees, which the display conversion has already capped at two
 * decimals.
 */
/**
 * The field a validator was called for, as a name path antd can look up.
 *
 * `rule.field` arrives dotted - `skuSizes.0.finishedWidth` - and `isFieldTouched` wants the
 * segments, with list indices as numbers.
 */
const namePathOf = (field: unknown): (string | number)[] | undefined =>
  typeof field === 'string' && field !== ''
    ? field.split('.').map((part) => (/^\d+$/.test(part) ? Number(part) : part))
    : undefined;

export const RULE_MEASURE_PRECISION: (
  precision: number,
  type: MeasureType,
  form?: { isFieldTouched: (name: (string | number)[]) => boolean }
) => Rule = (
  precision: number,
  type: MeasureType,
  form?: { isFieldTouched: (name: (string | number)[]) => boolean }
) => {
  const { t } = useTranslation();
  return {
    validator(rule: unknown, value: unknown) {
      if (value === null || value === undefined || value === '') {
        return Promise.resolve();
      }
      // Only what the user actually typed is held to this.
      //
      // These fields are pre-filled from what is stored, and a stored value can legitimately
      // carry more decimals than anyone may type: a size entered as 888mm is kept as the
      // 34.96063in it converts to, because the pricing engine works from that. The field shows
      // it shortened to 34.961, so flagging it told the user that a number they could see was
      // three decimals had too many - about data they never entered and cannot correct.
      //
      // Passing the form is what makes the difference knowable; without it the rule cannot tell
      // a typed value from a loaded one and falls back to checking every value.
      const path = namePathOf((rule as { field?: unknown })?.field);
      if (form && path && !form.isFieldTouched(path)) {
        return Promise.resolve();
      }
      // Counted on what was typed, not on what would be shown. This used to measure the display
      // value, which is rounded to three decimals before anyone sees it - so an entered 8.1234
      // was checked as 8.123, passed a three-decimal rule, and was saved with the fourth decimal
      // the rule exists to refuse.
      const raw = String(value).trim().replace(',', '.');
      if (raw === '' || !Number.isFinite(Number(raw))) {
        // Not a number at all. A different rule's job; this one only counts decimals.
        return Promise.resolve();
      }
      const decimals = (raw.split('.')[1] || '').length;
      return decimals > precision
        ? Promise.reject(new Error(t('common.precisionAllowed', { precision })))
        : Promise.resolve();
    },
  };
};

export const RULE_TEXT_LEN_255: Rule = {
  type: 'string',
  max: 255,
};
export const RULE_TEXT_FIXED_LEN: (length: number) => Rule = (
  length: number
) => {
  const { t } = useTranslation();
  return {
    type: 'string',
    max: 2,
    min: 2,
    message: t('common.fixedLenText', { length }),
  };
};
export const RULE_INTEGER_LIST: () => Rule = () => {
  const { t } = useTranslation();
  return {
    pattern: new RegExp(/(^(\d+)(\|\d+)*$)/),
    message: t('common.pipedInt'),
  };
};
export const RULE_YEAR: () => Rule = () => {
  const { t } = useTranslation();
  return {
    pattern: new RegExp('^([0-9]){4}$'),
    message: t('common.validYear'),
  };
};
export const RULE_DATETIME: () => Rule = () => {
  const { t } = useTranslation();
  return {
    pattern: new RegExp(
      '^(0[1-9]|1[0-2])\\/(0[1-9]|1\\d|2\\d|3[01])\\/(19|20)\\d{2}\\s(0\\d|1\\d|2[0-3]):([0-5]\\d|\\d)$'
    ),
    message: t('common.validDateTime'),
  };
};
export const RULE_SM_LG: (smFieldName: string, lgFieldName: string) => Rule = (
  smFieldName: string,
  lgFieldName: string
) => {
  const { t } = useTranslation();
  return ((form: FormInstance) => ({
    validator: () => {
      const s = Number(form.getFieldValue(smFieldName));
      const l = Number(form.getFieldValue(lgFieldName));
      if (s <= l) {
        return Promise.resolve();
      } else {
        return Promise.reject(t('common.largeSmall'));
      }
    },
  })) as Rule;
};
export const RULE_EMAIL_TRIM: (emailName: string) => Rule = (
  emailName: string
) => {
  const { t } = useTranslation();
  return ((form: FormInstance) => ({
    validator: () => {
      const email = (form.getFieldValue(emailName) || '').trim();
      const reg =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (!email || reg.test(email)) {
        return Promise.resolve();
      } else {
        return Promise.reject(t('common.emailValid'));
      }
    },
  })) as Rule;
};
